import {
  BalanceData, DAILY_BUY_ALL, DAILY_OFFER_IDS, DAILY_PAGE, DealsState, OfferItem, OfferPageDef, OfferPrice, Rotation, cheapestChoice, dailyDeal,
  gearPicksNeeded, gemPrices, hasRandomItem, lifetimeItems, offerOf, offerWithSeasonSkin, pageOf, periodOf, resolveOffer,
} from "@highmarch/domain";

/**
 * Resolves a Deals offer id (a catalog offer, a Daily Deals tier `daily.1..3` or `daily.all`) with the player's
 * choices into what it pays now, what its value counts, and its limits: the one place the Deals use cases
 * (deals.ts) and the grant read an offer, so the view, the intent and the grant can never disagree.
 */

const DAILY_LIMIT = 1;

export interface Resolved {
  readonly offerId: string;
  readonly page: OfferPageDef;
  readonly rotation: Rotation;
  readonly name: string;
  readonly price: OfferPrice;
  readonly limit: number;
  /** What it pays with the choices as made (unfilled picks and prize left out). */
  readonly items: readonly OfferItem[];
  /** What the value counts: unfilled picks and prize at their cheapest choice. */
  readonly valueItems: readonly OfferItem[];
  readonly choices: readonly OfferItem[];
  readonly prizes: readonly OfferItem[];
  readonly picks: readonly number[];
  readonly prize: number;
  readonly gearSlots: readonly number[];
  readonly gearSlotsNeeded: number;
  readonly complete: boolean;
  /**
   * Holds, or can be filled with, a random item: its fixed items, the chosen picks and prize, or any choice or prize
   * still on offer (Apple 3.1.1: odds before paying for whatever the player may pick; the no-random storefronts hide
   * the offer, since a grant without an intent may fill a pick with a random choice). The one odds rule.
   */
  readonly random: boolean;
  readonly theme: string;
  readonly wasCents: number | null;
  readonly weeklyTokens: number;
  readonly levelTrigger: number;
}

/**
 * `choices` overrides the state's choices (a grant from an intent). `fill`: an unfilled pick or prize takes its cheapest
 * choice and an unchosen gear slot slot 0 (a transaction with no intent is still granted, never lost).
 */
export function resolveDeal(balance: BalanceData, state: DealsState, playerId: string, offerId: string, now: number,
  choices: { theme?: string; picks?: readonly number[]; prize?: number; gearSlots?: readonly number[] } = {}, fill = false): Resolved | null {
  const data = balance.offers, slotCount = balance.gear.slots.length;
  if ((DAILY_OFFER_IDS as readonly string[]).includes(offerId) || offerId === DAILY_BUY_ALL) {
    const page = pageOf(data, DAILY_PAGE), d = page?.daily;
    if (!page || !d) return null;
    const theme = choices.theme ?? state.dailyTheme(data, playerId, now);
    const tier = offerId === DAILY_BUY_ALL ? "all" : DAILY_OFFER_IDS.indexOf(offerId as (typeof DAILY_OFFER_IDS)[number]);
    const deal = dailyDeal(d, theme, tier);
    if (!deal) return null;
    const themeName = d.themes.find((t) => t.id === theme)?.name ?? theme;
    return {
      offerId, page, rotation: "daily", name: tier === "all" ? "Buy All" : `${themeName} ${tier + 1}`, price: deal.price, limit: DAILY_LIMIT,
      items: deal.items, valueItems: deal.items, choices: [], prizes: [], picks: [], prize: -1, gearSlots: [], gearSlotsNeeded: 0, complete: true,
      random: hasRandomItem(deal.items), theme, wasCents: tier === "all" ? d.tiers.reduce((a, t) => a + t.price.cents, 0) : null, weeklyTokens: 0, levelTrigger: 0,
    };
  }
  // (the Master's Collection's rotating prize: a past season's skin in place of its skin prize, seasons.json mastersRotation)
  const listed = offerOf(data, offerId), def = listed ? offerWithSeasonSkin(balance, listed, now) : null, page = def ? pageOf(data, def.pageId) : null;
  if (!def || !page) return null;
  const prices = gemPrices(balance);
  let picks = [...(choices.picks ?? state.picks[offerId] ?? [])].slice(0, def.picks);
  let prize = choices.prize ?? state.prizes[offerId] ?? -1;
  const cheapest = (list: readonly OfferItem[]) => { const c = cheapestChoice(balance, list, prices); return c === null ? -1 : list.indexOf(c); };
  if (fill) {
    picks = Array.from({ length: def.picks }, (_, i) => (def.choices[picks[i] ?? -1] ? picks[i] : cheapest(def.choices)));
    if (def.prizes.length > 0 && !def.prizes[prize]) prize = cheapest(def.prizes);
  }
  let gearSlots = [...(choices.gearSlots ?? state.gearSlots[offerId] ?? [])];
  const pre = resolveOffer(def, page.rotation, now, picks, prize, [], slotCount);
  const need = gearPicksNeeded(pre.items);
  if (fill) gearSlots = Array.from({ length: need }, (_, i) => (Number.isInteger(gearSlots[i]) && gearSlots[i] >= 0 && gearSlots[i] < slotCount ? gearSlots[i] : 0));
  const r = resolveOffer(def, page.rotation, now, picks, prize, gearSlots, slotCount);
  // a card's daily items and a fund's milestones count too (MP-01..03: what the purchase pays over its life)
  const valueItems = lifetimeItems(def);
  for (let i = 0; i < def.picks; i++) { const c = def.choices[picks[i] ?? -1] ?? def.choices[cheapest(def.choices)]; if (c) valueItems.push(c); }
  if (def.prizes.length > 0) { const p = def.prizes[prize] ?? def.prizes[cheapest(def.prizes)]; if (p) valueItems.push(p); }
  return {
    offerId, page, rotation: page.rotation, name: def.name, price: def.price, limit: def.limit, items: r.items, valueItems, choices: def.choices, prizes: def.prizes,
    picks: Array.from({ length: def.picks }, (_, i) => (def.choices[picks[i] ?? -1] ? picks[i] : -1)), prize: def.prizes[prize] ? prize : -1,
    gearSlots: r.gearSlots, gearSlotsNeeded: need, complete: r.complete, random: hasRandomItem(valueItems) || hasRandomItem(def.choices) || hasRandomItem(def.prizes), theme: "", wasCents: null,
    weeklyTokens: def.weeklyTokens, levelTrigger: def.levelTrigger,
  };
}

/** The offer ids a page lists, in order (Daily Deals: the three tiers, then Buy All). */
export function pageOfferIds(page: OfferPageDef): string[] {
  return page.daily ? [...DAILY_OFFER_IDS, DAILY_BUY_ALL] : page.offers.map((o) => o.id);
}

/** The period a purchase at `atSeconds` counts in. */
export function periodKeyAt(rotation: Rotation, atSeconds: number): number { return periodOf(rotation, atSeconds).key; }

/** Every Deals product id (offers, Daily Deals tiers and Buy All), for the Store's product list. */
export function dealProducts(balance: BalanceData): Map<string, { readonly offerId: string; readonly price: OfferPrice; readonly name: string }> {
  const out = new Map<string, { offerId: string; price: OfferPrice; name: string }>();
  for (const p of balance.offers.pages) {
    for (const o of p.offers) out.set(o.price.product, { offerId: o.id, price: o.price, name: o.name });
    if (p.daily) {
      p.daily.tiers.forEach((t, i) => out.set(t.price.product, { offerId: DAILY_OFFER_IDS[i] ?? `${DAILY_PAGE}.${i + 1}`, price: t.price, name: `Daily Deal ${i + 1}` }));
      out.set(p.daily.buyAll.product, { offerId: DAILY_BUY_ALL, price: p.daily.buyAll, name: "Daily Deals: Buy All" });
    }
  }
  return out;
}

/**
 * Every real-money product the game sells (gem packs, then Deals, then the season pass), with its US reference price: the list the Store's
 * `products()` is asked for, the sandbox store's catalogue and the StoreKit configuration (tools/web/check-ios.ts).
 */
export function allStoreProducts(balance: BalanceData): { readonly id: string; readonly displayName: string; readonly referencePrice: string }[] {
  return [
    ...balance.commerce.shop.gemPacks.map((p) => ({ id: p.id, displayName: p.name, referencePrice: p.referencePrice })),
    ...[...dealProducts(balance)].map(([id, p]) => ({ id, displayName: p.name, referencePrice: p.price.label })),
    // The season pass (MP-22, seasons.json): Premium, then Premium+.
    ...[balance.seasons.premium, balance.seasons.premiumPlus].filter((p) => p.price.product !== "").map((p) => ({ id: p.price.product, displayName: p.name, referencePrice: p.price.label })),
  ];
}
